import React, { useEffect } from 'react';
import { DEFAULT_OG_IMAGE, DEFAULT_SITE_DESCRIPTION, SITE_NAME } from '../../utils/seo';

type StructuredData = Record<string, unknown> | Array<Record<string, unknown>>;

interface SeoHeadProps {
  title: string;
  description?: string;
  canonicalUrl?: string;
  imageUrl?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  noIndex?: boolean;
  structuredData?: StructuredData;
}

const removeElement = (selector: string) => {
  document.head.querySelector(selector)?.remove();
};

const upsertMetaTag = (selector: string, attributes: Record<string, string>) => {
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!meta) {
    meta = document.createElement('meta');
    document.head.appendChild(meta);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    meta!.setAttribute(key, value);
  });
};

const setMetaByName = (name: string, content?: string) => {
  const selector = `meta[name="${name}"]`;
  if (!content) {
    removeElement(selector);
    return;
  }

  upsertMetaTag(selector, { name, content });
};

const setMetaByProperty = (property: string, content?: string) => {
  const selector = `meta[property="${property}"]`;
  if (!content) {
    removeElement(selector);
    return;
  }

  upsertMetaTag(selector, { property, content });
};

const setLinkTag = (rel: string, href?: string) => {
  const selector = `link[rel="${rel}"]`;

  if (!href) {
    removeElement(selector);
    return;
  }

  let link = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    document.head.appendChild(link);
  }

  link.setAttribute('rel', rel);
  link.setAttribute('href', href);
};

const setStructuredData = (structuredData?: StructuredData) => {
  const selector = 'script[data-seo-structured-data="true"]';

  if (!structuredData) {
    removeElement(selector);
    return;
  }

  let script = document.head.querySelector(selector) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-structured-data', 'true');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(structuredData).replace(/</g, '\\u003c');
};

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description = DEFAULT_SITE_DESCRIPTION,
  canonicalUrl,
  imageUrl = DEFAULT_OG_IMAGE,
  type = 'website',
  publishedTime,
  modifiedTime,
  section,
  noIndex = false,
  structuredData,
}) => {
  const structuredDataJson = structuredData ? JSON.stringify(structuredData) : '';

  useEffect(() => {
    const structuredDataPayload = structuredDataJson
      ? (JSON.parse(structuredDataJson) as StructuredData)
      : undefined;

    document.title = title;

    setMetaByName('description', description);
    setMetaByName('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    setLinkTag('canonical', canonicalUrl);

    setMetaByProperty('og:site_name', SITE_NAME);
    setMetaByProperty('og:type', type);
    setMetaByProperty('og:title', title);
    setMetaByProperty('og:description', description);
    setMetaByProperty('og:url', canonicalUrl);
    setMetaByProperty('og:image', imageUrl);

    setMetaByName('twitter:card', imageUrl ? 'summary_large_image' : 'summary');
    setMetaByName('twitter:title', title);
    setMetaByName('twitter:description', description);
    setMetaByName('twitter:image', imageUrl);

    setMetaByProperty('article:published_time', type === 'article' ? publishedTime : undefined);
    setMetaByProperty('article:modified_time', type === 'article' ? modifiedTime : undefined);
    setMetaByProperty('article:section', type === 'article' ? section : undefined);

    setStructuredData(structuredDataPayload);
  }, [
    canonicalUrl,
    description,
    imageUrl,
    modifiedTime,
    noIndex,
    publishedTime,
    section,
    structuredDataJson,
    title,
    type,
  ]);

  return null;
};

export default SeoHead;
